

import { defineStore } from 'pinia';
import { getMenusApi } from '@/api/user';
import defaultSettings from '@/settings';
import {
  crmebMenuToSidebarRoutes,
  crmebNodesToNavList,
  filterMenuByAccess,
  normalizeAppPath,
  type CrmebMenuNode,
  type CrmebNavMenuItem,
} from '@/utils/crmebMenu';
import { filterMenuByVenueFeatures } from '@/utils/residentVenueMenu';
import useUserStore from '@/store/modules/user';
import {
  findFirstNonNullChildren,
  getHeaderName,
  getMenuSider,
} from '@/utils/system';
import useCrmebThemeConfigStore from '@/store/modules/themeConfig';
import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import router from '@/router';


//eb
const MENU_STORAGE_KEY = 'MerMenuList';

const useCrmebMenuStore = defineStore('crmebMenu', {
  state: (): {
    menuTree: CrmebMenuNode[];
    sidebarRouters: RouteRecordRaw[];
    /** EB 分栏顶栏菜单 */
    columnsNavList: CrmebNavMenuItem[];
    /** EB 分栏左侧子菜单 */
    childNavList: CrmebNavMenuItem[];
    activeColumnIndex: number;
    access: string[];
    loaded: boolean;
  } => ({
    menuTree: [],
    sidebarRouters: [],
    columnsNavList: [],
    childNavList: [],
    activeColumnIndex: 0,
    access: [],
    loaded: false,
  }),
  actions: {
    setAccess(access: string[]) {
      this.access = access || [];
    },
    /** 按 access + venueMode features 重建侧栏 */
    rebuildSidebarFromTree(rawTree: CrmebMenuNode[]) {
      const userStore = useUserStore();
      let filtered = this.access.length
        ? filterMenuByAccess(rawTree, this.access)
        : rawTree;
      filtered = filterMenuByVenueFeatures(filtered, userStore.venueFeatures);
      this.sidebarRouters = crmebMenuToSidebarRoutes(
        filtered,
        defaultSettings.roterPre,
      ) as RouteRecordRaw[];
      this.columnsNavList = crmebNodesToNavList(filtered, defaultSettings.roterPre).map(
        (v, k) => ({ ...v, k }),
      );
      if (router.currentRoute.value?.path) {
        this.syncLayoutByRoute(router.currentRoute.value);
      }
    },
    /** venueMode 切换后重算侧栏（menuTree 保持 API 原始树） */
    refreshVenueMenuFilter() {
      if (!this.menuTree.length) {
        return;
      }
      this.rebuildSidebarFromTree(this.menuTree);
    },
    setMenuTree(tree: CrmebMenuNode[]) {
      this.menuTree = tree || [];
      try {
        localStorage.setItem(MENU_STORAGE_KEY, JSON.stringify(this.menuTree));
      } catch {
        // ignore
      }
      this.rebuildSidebarFromTree(this.menuTree);
      this.loaded = true;
    },
    /** 按当前路由同步分栏高亮与子菜单（仅 columns / classic） */
    syncLayoutByRoute(to: RouteLocationNormalized) {
      const layout = useCrmebThemeConfigStore().themeConfig.layout;
      if (layout !== 'columns' && layout !== 'classic') {
        return;
      }
      const list = this.columnsNavList;
      if (!list.length) {
        this.childNavList = [];
        return;
      }
      const headerName = getHeaderName(to, list);
      const column =
        list.find((item) => item.path === headerName) ||
        list[0];
      const idx = column?.k ?? 0;
      this.activeColumnIndex = idx;
      const sider = getMenuSider(list, column?.path || list[0].path);
      const asideChildren = sider[0]?.children || [];
      this.childNavList = asideChildren;
      const theme = useCrmebThemeConfigStore();
      // 对齐 EB columnsAside 路由同步：有二级即展开（勿用 >1，否则「员工」下仅「店员管理」时侧栏 width:0）
      theme.setCollapse(asideChildren.length > 0 ? false : true);
    },
    onColumnClick(item: CrmebNavMenuItem) {
      const theme = useCrmebThemeConfigStore();
      if (item.children?.length) {
        const first = findFirstNonNullChildren(item.children);
        if (first?.path) {
          router.push(normalizeAppPath(first.path));
        }
        // 对齐 EB Aside menuList 兜底：childMenuList.length > 0 强制展开
        theme.setCollapse(item.children.length > 0 ? false : true);
        this.childNavList = item.children;
        this.activeColumnIndex = item.k ?? 0;
      } else if (item.path) {
        router.push(normalizeAppPath(item.path));
        theme.setCollapse(true);
        this.childNavList = [];
        this.activeColumnIndex = item.k ?? 0;
      }
    },
    restoreFromStorage() {
      try {
        const raw = localStorage.getItem(MENU_STORAGE_KEY);
        if (raw) {
          const tree = JSON.parse(raw) as CrmebMenuNode[];
          if (Array.isArray(tree) && tree.length) {
            this.setMenuTree(tree);
            return true;
          }
        }
      } catch {
        // ignore
      }
      return false;
    },
    clear() {
      this.menuTree = [];
      this.sidebarRouters = [];
      this.columnsNavList = [];
      this.childNavList = [];
      this.activeColumnIndex = 0;
      this.access = [];
      this.loaded = false;
      try {
        localStorage.removeItem(MENU_STORAGE_KEY);
      } catch {
        // ignore
      }
    },
    /** 拉取 EB 菜单树 */
    loadMenus() {
      return getMenusApi().then((res: any) => {
        const tree = (res?.data ?? res ?? []) as CrmebMenuNode[];
        this.setMenuTree(Array.isArray(tree) ? tree : []);
        return this.menuTree;
      });
    },
  },
});

export default useCrmebMenuStore;
